import {useState} from "react"
import {Typography, Button, Box} from "@mui/material"
import {styled} from "@mui/material/styles"
import {LoadingPage} from "@indebted/components/Loading"
import {useTranslation, Trans} from "react-i18next"
import {ErrorPage} from "@indebted/components/Error"
import {useAnalytics} from "@indebted/analytics"
import {Panel, PanelDivider} from "@indebted/components/Panel"
import {PageTitle, PageSubtitle, NewLayoutPage} from "@indebted/components/Page"

import {formatCurrency} from "../../../src/currencyFormatter"
import {formatDate} from "../../../src/dateFormatter"
import {Breadcrumbs} from "../Breadcrumbs"
import {ValidLocale} from "../../../src/Locales"
import {usePaymentArrangementId} from "../usePaymentArrangementId"

import {DateButton} from "./DateButton"
import {PaymentSchedule} from "./PaymentSchedule"
import {FrequencyToggleGroup} from "./FrequencyToggleGroup"
import {getMaxDate, type Frequency} from "./Frequency"
import {NumberOfPaymentSlider} from "./NumberOfPaymentSlider"
import {useSetupQueryOrCalculate, useSetupConfig, useSetupMutation} from "./useSetupQueryOrCalculate"

const SliderPage = () => {
	const paymentArrangementID = usePaymentArrangementId()
	const {t, i18n} = useTranslation()
	const {track} = useAnalytics()

	const {data: setupConfig, error: setupConfigError} = useSetupConfig(paymentArrangementID)

	const [_frequency, setFrequency] = useState<Frequency | null>(null)
	const [_sliderValue, setSliderValue] = useState<number | null>(null)
	const [_repayments, setRepayments] = useState<number | null>(null)
	const [startDate, setStartDate] = useState<Date>(new Date())

	const frequency = _frequency ?? setupConfig?.RecommendedFrequency ?? "Monthly"
	const sliderValue = _sliderValue ?? setupConfig?.Recommendations[frequency].NumberOfPayments ?? 1
	const repayments = _repayments ?? setupConfig?.Recommendations[frequency].NumberOfPayments ?? 1

	const {
		data: setupPage,
		isFetching: isFetchingSetupPage,
		error: setupError,
	} = useSetupQueryOrCalculate({
		paymentArrangementID,
		frequency,
		repayments,
		startDate,
		configuration: setupConfig,
		enabled: !!paymentArrangementID && !!setupConfig,
		sliderValue,
	})

	const {mutate: setupMutation, isPending: isPendingSetupMutation} = useSetupMutation({
		paymentArrangementID,
		frequency,
		startDate,
		numberOfPayments: repayments,
	})

	const handleFrequencyChange = (newFreq: Frequency) => {
		setFrequency(newFreq)
		setSliderValue(null)
		setRepayments(null)
		setStartDate(new Date())

		track("payment_arrangement_setup_page.frequency_changed", {
			paymentArrangementID,
			frequency: newFreq,
		})
	}

	const handleSliderChange = (value: number) => {
		setSliderValue(value)
		track("payment_arrangement_setup_page.slider_changed", {
			paymentArrangementID,
			frequency,
			numberOfPayments: value,
		})
	}

	const handleSliderChangeCommitted = (value: number) => {
		setSliderValue(value)
		setRepayments(value)
	}

	const handleDateChange = (newStartDate: Date) => {
		setStartDate(newStartDate)
		track("payment_arrangement_setup_page.start_date_changed", {
			paymentArrangementID,
			startDate: newStartDate,
		})
	}

	const handleCreateButtonClick = () => {
		setupMutation()
		track("payment_arrangement_setup_page.create_button_clicked", {
			paymentArrangementID,
			frequency,
			numberOfPayments: repayments,
			startDate: startDate,
		})
	}

	if (setupConfigError || setupError) {
		return <ErrorPage error={setupConfigError || setupError} />
	}

	if (!setupPage || !setupConfig) {
		return <LoadingPage />
	}

	const maxDate = getMaxDate(frequency)

	return (
		<NewLayoutPage>
			<PrimaryContainer>
				<Box sx={{marginBottom: "1rem"}}>
					<Breadcrumbs currentStep={t("breadcrumbs.set_up_plan")} />
				</Box>
				<PageTitle>{t("payment_arrangement_setup_page.title")}</PageTitle>
				<PageSubtitle>
					<Trans
						i18nKey="payment_arrangement_setup_page.subtitle"
						values={{
							OutstandingBalance: formatCurrency(
								setupConfig.OutstandingBalance / 100,
								setupConfig.Currency,
								i18n.language as ValidLocale,
							),
							ClientName: setupConfig.ClientName,
						}}
						components={{bold: <strong />}}
					/>
				</PageSubtitle>
				<Panel sx={{marginTop: "2rem"}}>
					<Box>
						<FrequencyLabel>
							{t("payment_arrangement_setup_page.frequency")}{" "}
							{isFetchingSetupPage ? t("payment_arrangement_setup_page.frequency_loading") : ""}
						</FrequencyLabel>
						<FrequencyToggleGroup frequency={frequency} handleFrequencyChange={handleFrequencyChange} />
					</Box>
					<PanelDivider />
					<NumberOfPaymentSlider
						setupPage={setupPage}
						maxDuration={setupConfig.Recommendations[frequency].MaxDuration}
						sliderValue={sliderValue}
						handleSliderChange={handleSliderChange}
						handleSliderChangeCommitted={handleSliderChangeCommitted}
					/>
					<PanelDivider />
					<Box>
						<Box marginBottom="0.5rem">
							<FrequencyLabel>{t("payment_arrangement_setup_page.start_date")}</FrequencyLabel>
							<DateButton startDate={startDate} maxDate={maxDate} onDateChanged={handleDateChange} />
						</Box>

						<EndDate>
							<Trans
								i18nKey="payment_arrangement_setup_page.end_date"
								values={{
									EndDate: formatDate(new Date(setupPage.EndDate)),
								}}
								components={{bold: <strong />}}
							/>
						</EndDate>
					</Box>

					<PanelDivider />

					<PlanDetails sx={{marginBottom: "1rem"}}>
						<Trans
							i18nKey={`payment_arrangement_setup_page.plan_details.${frequency}`}
							values={{
								StartDate: formatDate(startDate),
								EndDate: formatDate(new Date(setupPage.EndDate)),
								PaymentAmount: formatCurrency(
									setupPage.PaymentAmount / 100,
									setupConfig.Currency,
									i18n.language as ValidLocale,
								),
								Frequency: frequency,
								TotalPayments: sliderValue,
								count: sliderValue,
							}}
							components={{bold: <strong />}}
						/>
					</PlanDetails>

					<CreateButton
						variant="outlined"
						disabled={isFetchingSetupPage || isPendingSetupMutation}
						onClick={handleCreateButtonClick}
					>
						{t("payment_arrangement_setup_page.create_button")}
					</CreateButton>
				</Panel>
				<Panel sx={{marginTop: "2rem", marginBottom: "3rem"}}>
					<PaymentSchedule setupPage={setupPage} />
				</Panel>
			</PrimaryContainer>
		</NewLayoutPage>
	)
}

const PrimaryContainer = styled("div")`
	flex-direction: column;
	display: flex;
`

const FrequencyLabel = styled(Typography)`
	font-size: 0.9375rem;
	font-weight: 500;
	margin-bottom: 0.75rem;
`

const EndDate = styled(Typography)`
	color: #6b7280;
	font-size: 0.8125rem;
	font-weight: 400;
	line-height: 1rem;

	strong {
		color: #474cde;
		font-size: 0.8125rem;
		font-weight: 500;
		line-height: 1rem;
	}
`

const PlanDetails = styled(Box)`
	width: 100%;
	color: #000244;
	text-align: center;
	font-size: 0.9375rem;
	font-weight: 400;
	line-height: 1.42188rem;
	border-radius: 0.5rem;
	background: rgba(189, 191, 242, 0.2);
	padding: 1rem;

	strong {
		color: #474cde;
		font-size: 0.9375rem;
		font-weight: 600;
		line-height: 1.42188rem;
	}
`

const CreateButton = styled(Button)`
	width: 100%;
	background-color: #474cde;
	color: white;
	font-weight: 700;
	font-size: 16px;
	padding: 15px;
	text-transform: none;
	border-radius: 8px;
	border: 1px solid #474cde;
	&:hover {
		background: #2e2ee8;
	}
	&:disabled {
		background-color: #474cde;
		color: white;
	}
`

export {SliderPage}
